
import { useState, useEffect } from "react";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";
import { useAuth } from "@/contexts/AuthContext";

export function usePEOProviderData() {
  const { user } = useAuth();
  const [loading, setLoading] = useState(true);
  const [providerInfo, setProviderInfo] = useState<any>(null);
  const [isEditing, setIsEditing] = useState(false);
  const [form, setForm] = useState({
    name: '',
    contact_email: '',
    years_in_business: '',
    employee_range: '',
  });
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    const fetchProviderInfo = async () => {
      if (!user) return;
      try {
        setLoading(true);
        setIsEditing(false);
        
        // First, get the provider ID associated with this user
        const { data: repData, error: repError } = await supabase
          .from('peo_representatives')
          .select('provider_id, verification_status')
          .eq('user_id', user.id)
          .maybeSingle();
          
        if (repError) {
          console.error("Error fetching representative data:", repError);
          toast.error("Failed to load provider information");
          setProviderInfo(null);
          return;
        }
        
        if (!repData || !repData.provider_id) {
          console.log("No provider associated with this user");
          setProviderInfo(null);
          return;
        }
        
        // Then get the provider details
        const { data: provider, error: providerError } = await supabase
          .from('peo_providers')
          .select('*')
          .eq('id', repData.provider_id)
          .maybeSingle();
          
        if (providerError) {
          console.error("Error fetching provider data:", providerError);
          toast.error("Failed to load provider details");
          setProviderInfo(null);
          return;
        }
        
        if (!provider) {
          console.log("Provider not found with ID:", repData.provider_id);
          setProviderInfo(null);
          return;
        }
        
        setProviderInfo(provider);
        setForm({
          name: provider.name || '',
          contact_email: provider.contact_email || '',
          years_in_business: provider.years_in_business ? String(provider.years_in_business) : '',
          employee_range: provider.employee_range || '',
        });
      } catch (err: any) {
        console.error("Unexpected error in fetchProviderInfo:", err);
        toast.error(`Error: ${err.message || 'Failed to load provider information'}`);
      } finally {
        setLoading(false);
      }
    };
    
    fetchProviderInfo();
  }, [user]);

  const handleSave = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!providerInfo?.id) return;
    
    setSaving(true);
    try {
      const updates = {
        name: form.name.trim(),
        contact_email: form.contact_email.trim(),
        years_in_business: form.years_in_business ? parseInt(form.years_in_business, 10) : null,
        employee_range: form.employee_range.trim(),
      };
      
      const { error } = await supabase
        .from('peo_providers')
        .update(updates)
        .eq('id', providerInfo.id);
        
      if (error) {
        console.error("Error updating provider:", error);
        toast.error('Failed to update provider profile.');
      } else {
        toast.success('Profile updated successfully!');
        setProviderInfo({ ...providerInfo, ...updates });
        setIsEditing(false);
      }
    } catch (err: any) {
      console.error("Unexpected error in handleSave:", err);
      toast.error(`Error: ${err.message || 'Failed to save changes'}`);
    } finally {
      setSaving(false);
    }
  };

  return {
    loading,
    providerInfo,
    isEditing,
    setIsEditing,
    form,
    setForm,
    handleSave,
    saving,
  };
}
